import { useState } from 'react';

import { Button } from '../../button';

import { Lab, Panel, Reset, Result } from './lab-parts';
import { ModelSelect } from './learning-model-surface';
import {
  applyTransform,
  initialPipeline,
  narrowingFrame,
  pipelineStatus,
  pipelineTransition,
  projectTaskType,
  scriptTasks,
  transformExpressions,
  typeCheckRecord,
  type AssigneeInput,
  type NarrowingCode,
  type NarrowingTask,
  type PipelineAction,
  type ScriptTask,
  type TransformFrame,
  type TransformOperation,
} from './script-models';

import './database-labs.css';

function Predict({ children }: { children: string }) {
  return (
    <p className="db-predict">
      <strong>Predict first</strong> {children}
    </p>
  );
}

function Rows({ rows, label }: { rows: ScriptTask[]; label: string }) {
  return (
    <div role="group" aria-label={label}>
      <div className="db-rows cb-record-grid" role="table" aria-label={label}>
        <div role="row" className="db-row db-row-heading">
          <span role="columnheader">id</span>
          <span role="columnheader">title</span>
          <span role="columnheader">completed</span>
        </div>
        {rows.map((row) => (
          <div role="row" className="db-row" key={row.id}>
            <span role="cell">{row.id}</span>
            <span role="cell">{row.title}</span>
            <span role="cell">{String(row.completed)}</span>
          </div>
        ))}
        {rows.length === 0 && (
          <div role="row" className="db-empty">
            <span role="cell">No rows</span>
          </div>
        )}
      </div>
    </div>
  );
}

function Facts({ values }: { values: [string, string | number][] }) {
  return (
    <dl className="cb-memory">
      {values.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function TransformResultView({ frame }: { frame: TransformFrame }) {
  if (frame.result.kind === 'tasks') return <Rows rows={frame.result.values} label="Result" />;
  if (frame.result.kind === 'titles')
    return <pre aria-label="Result">{JSON.stringify(frame.result.values, null, 2)}</pre>;
  return (
    <pre aria-label="Result">
      {frame.result.value === undefined ? 'undefined' : JSON.stringify(frame.result.value, null, 2)}
    </pre>
  );
}

export function ScriptTransformsLab() {
  const [operation, setOperation] = useState<TransformOperation>('map');
  const [frame, setFrame] = useState<TransformFrame | null>(null);
  const stored = frame ? frame.stored : scriptTasks();
  return (
    <Lab
      title="Does transforming a list change the stored list?"
      eyebrow="JavaScript · array transforms"
      className="db-lab"
    >
      <Predict>
        Sorting the task list by title for one view: does the stored array keep its original order
        afterwards?
      </Predict>
      <div className="cb-controls">
        <label>
          Operation
          <ModelSelect
            aria-label="Array operation"
            value={operation}
            onValueChange={(value) => {
              setOperation(value as TransformOperation);
              setFrame(null);
            }}
            options={[
              { value: 'map', label: 'map · titles only' },
              { value: 'filter', label: 'filter · open tasks' },
              { value: 'find', label: 'find · the task with id 4' },
              { value: 'sort', label: 'sort · by title' },
              { value: 'copy-sort', label: '[...tasks].sort · copy, then sort' },
            ]}
          />
        </label>
      </div>
      <pre aria-label="JavaScript expression">{transformExpressions[operation]}</pre>
      <div className="cb-two">
        <Panel label="Stored array · tasks">
          <Rows rows={stored} label="Stored tasks" />
        </Panel>
        <Panel label="Result of the expression">
          {frame ? (
            <TransformResultView frame={frame} />
          ) : (
            <p>Apply the expression to see what it returns.</p>
          )}
        </Panel>
      </div>
      {frame ? (
        <Facts
          values={[
            ['Stored array changed', frame.storedChanged ? 'yes' : 'no'],
            ['Result is the stored array itself', frame.sameArray ? 'yes' : 'no'],
          ]}
        />
      ) : null}
      <div className="cb-controls">
        <Button
          size="sm"
          disabled={frame !== null}
          onClick={() => setFrame(applyTransform(operation))}
        >
          Apply expression
        </Button>
        <Reset
          onClick={() => {
            setOperation('map');
            setFrame(null);
          }}
        />
      </div>
      <Result tone={frame ? (frame.storedChanged ? 'warning' : 'success') : 'neutral'}>
        {frame
          ? frame.message
          : 'Choose an operation, predict whether the stored array will change, then apply it. Changing the operation restores the original three records.'}
      </Result>
      <p className="db-caption">
        map, filter and find return new values and leave the stored array alone, like a SELECT
        leaves a table alone. sort is the exception: it reorders the array it was called on. Copy
        first when the stored order matters elsewhere.
      </p>
    </Lab>
  );
}

export function TypesCheckLab() {
  const [assignee, setAssignee] = useState<AssigneeInput>('number');
  const [misspelt, setMisspelt] = useState(false);
  const [checked, setChecked] = useState(false);
  const frame = typeCheckRecord(assignee, misspelt);
  return (
    <Lab
      title="Which mistakes does a type catch before the page runs?"
      eyebrow="TypeScript · a record type"
      className="db-lab"
    >
      <Predict>
        The API sends assignee_id as a number or null. If the page stores the string &apos;2&apos;
        instead, which tool notices first: tsc or the browser?
      </Predict>
      <div className="cb-controls">
        <label>
          Value for assignee_id
          <ModelSelect
            aria-label="Value for assignee_id"
            value={assignee}
            onValueChange={(value) => {
              setAssignee(value as AssigneeInput);
              setChecked(false);
            }}
            options={[
              { value: 'number', label: '2 · a number' },
              { value: 'null', label: 'null · no assignee' },
              { value: 'string', label: "'2' · a string" },
              { value: 'undefined', label: 'undefined · field left unset' },
            ]}
          />
        </label>
        <Button
          size="sm"
          variant="secondary"
          aria-pressed={misspelt}
          onClick={() => {
            setMisspelt(!misspelt);
            setChecked(false);
          }}
        >
          Misspell it as asignee_name (one s)
        </Button>
      </div>
      <div className="cb-two">
        <Panel label="Type · what a ProjectTask must contain">
          <pre aria-label="ProjectTask type">{projectTaskType}</pre>
        </Panel>
        <Panel label="Record · the value being written">
          <pre aria-label="Task record">{frame.literal}</pre>
        </Panel>
      </div>
      <Panel label="tsc output">
        {checked ? (
          <pre aria-label="Checker output">
            {frame.accepted
              ? 'No errors. The record matches ProjectTask.'
              : frame.errors.join('\n')}
          </pre>
        ) : (
          <p>Run the checker to see whether this record is accepted.</p>
        )}
      </Panel>
      <div className="cb-controls">
        <Button size="sm" disabled={checked} onClick={() => setChecked(true)}>
          Check with tsc
        </Button>
        <Reset
          onClick={() => {
            setAssignee('number');
            setMisspelt(false);
            setChecked(false);
          }}
        />
      </div>
      <Result tone={checked ? (frame.accepted ? 'success' : 'warning') : 'neutral'}>
        {checked
          ? frame.runtime
          : 'Choose a value and decide whether tsc should accept it. Then compare with what the browser would do at run time.'}
      </Result>
      <p className="db-caption">
        The checker reads the types in the source before anything runs; the browser never sees them.
        A rejected record is a mistake found at your desk instead of in Maya&apos;s browser.
      </p>
    </Lab>
  );
}

export function TypesNarrowingLab() {
  const [task, setTask] = useState<NarrowingTask>('assigned');
  const [code, setCode] = useState<NarrowingCode>('direct');
  const [ran, setRan] = useState(false);
  const frame = narrowingFrame(task, code);
  return (
    <Lab
      title="Can you read assignee_name.length without checking?"
      eyebrow="TypeScript · narrowing a nullable value"
      className="db-lab"
    >
      <Predict>
        assignee_name is string | null. Does tsc accept task.assignee_name.length for a task that
        happens to be assigned?
      </Predict>
      <div className="cb-controls">
        <label>
          Task
          <ModelSelect
            aria-label="Task record"
            value={task}
            onValueChange={(value) => {
              setTask(value as NarrowingTask);
              setRan(false);
            }}
            options={[
              { value: 'assigned', label: 'Task 8 · assigned to Ravi' },
              { value: 'unassigned', label: 'Task 9 · unassigned (null)' },
            ]}
          />
        </label>
        <label>
          Code
          <ModelSelect
            aria-label="Code variant"
            value={code}
            onValueChange={(value) => {
              setCode(value as NarrowingCode);
              setRan(false);
            }}
            options={[
              { value: 'direct', label: 'Use .length directly' },
              { value: 'check', label: 'Check === null first' },
              { value: 'optional', label: 'Optional chaining with a fallback' },
            ]}
          />
        </label>
      </div>
      <pre aria-label="Code">{frame.code}</pre>
      <div className="cb-two">
        <Panel label="tsc verdict">
          {ran ? (
            <pre aria-label="Checker verdict">{frame.verdict}</pre>
          ) : (
            <p>Run to see whether the checker accepts this code.</p>
          )}
        </Panel>
        <Panel label="Result for the chosen task">
          <Facts values={[['assignee_name', frame.assigneeName ?? 'null']]} />
          {ran ? (
            <pre aria-label="Runtime result">{frame.runtime}</pre>
          ) : (
            <p>Run to see what the browser would compute.</p>
          )}
        </Panel>
      </div>
      <div className="cb-controls">
        <Button size="sm" disabled={ran} onClick={() => setRan(true)}>
          Run the checker and the code
        </Button>
        <Reset
          onClick={() => {
            setTask('assigned');
            setCode('direct');
            setRan(false);
          }}
        />
      </div>
      <Result tone={ran ? (frame.accepted ? 'success' : 'warning') : 'neutral'}>
        {ran
          ? frame.message
          : 'Predict the verdict for each task, then run. Try the direct line with the unassigned task.'}
      </Result>
      <p className="db-caption">
        Narrowing means proving, in code, which of the possible types a value has right now. The
        checker follows your if and ?. and allows only what the proof covers.
      </p>
    </Lab>
  );
}

export function BuildPipelineLab() {
  const [state, setState] = useState(initialPipeline);
  const act = (action: PipelineAction) =>
    setState((current) => pipelineTransition(current, action));
  const status = pipelineStatus(state);
  return (
    <Lab
      title="What does the browser run after you save a file?"
      eyebrow="Toolchain · source, bundle and the Open app tab"
      className="db-lab"
    >
      <Predict>
        You save a change in TaskPage.tsx and press reload in the Open app tab. Does the tab show
        the change?
      </Predict>
      <div className="db-three">
        <Panel label="Source · web/src/TaskPage.tsx">
          <Facts
            values={[
              ['Saved version', `v${state.source}`],
              ['Runs in the browser?', 'never · TypeScript and JSX are not browser code'],
            ]}
          />
        </Panel>
        <Panel label="Bundle · dist/">
          <Facts
            values={[
              ['Built version', `v${state.built}`],
              ['Matches the source?', status.bundleStale ? 'no · rebuild needed' : 'yes'],
            ]}
          />
        </Panel>
        <Panel label="Open app tab">
          <Facts
            values={[
              ['Running version', `v${state.shown}`],
              ['Matches dist/?', status.tabStale ? 'no · reload needed' : 'yes'],
            ]}
          />
        </Panel>
      </div>
      <div className="cb-controls">
        <Button size="sm" onClick={() => act('edit')}>
          Edit and save TaskPage.tsx
        </Button>
        <Button size="sm" variant="secondary" onClick={() => act('rebuild')}>
          Rebuild (npm run build)
        </Button>
        <Button size="sm" variant="secondary" onClick={() => act('reload')}>
          Reload the Open app tab
        </Button>
      </div>
      <div className="cb-controls">
        <Button
          size="sm"
          variant="secondary"
          aria-pressed={state.watch}
          onClick={() => act('toggle-watch')}
        >
          npm run watch running
        </Button>
        <Reset onClick={() => act('reset')} />
      </div>
      <Result tone={status.bundleStale || status.tabStale ? 'warning' : 'success'}>
        {state.message}
      </Result>
      <p className="db-caption">
        FastAPI serves dist/ only. The Open app tab receives the last built bundle, never a .tsx
        source file. A saved edit reaches the page only after a rebuild and a reload.
      </p>
    </Lab>
  );
}
